import { useState, useCallback, useMemo } from 'react'
import { getIdCuenta } from '../util/idCuenta'

/**
 * Hook para administrar las operaciones de cuentas pendientes de guardar.
 *
 * La acción de eliminar cuentas de la grilla de "Mis cuentas" se
 * refleja únicamente de manera visual hasta que el usuario presione
 * "Guardar cambios", que es el punto único de persistencia.
 *
 * - cuentasEliminadas: cuentas presentes en el servidor marcadas para baja.
 *
 * El estado vive en memoria: si el usuario actualiza la página o abandona la
 * pantalla sin guardar, las operaciones no confirmadas se descartan solas.
 */
export const useCuentasPendientes = () => {
  const [cuentasEliminadas, setCuentasEliminadas] = useState([])

  /**
   * Marca una cuenta del servidor como pendiente de baja (solo visual).
   * Es idempotente: si ya está marcada, no se duplica.
   */
  const marcarEliminada = useCallback((cuenta) => {
    if (!cuenta) return
    const idCuenta = getIdCuenta(cuenta)
    setCuentasEliminadas((prev) => {
      if (prev.some((c) => getIdCuenta(c) === idCuenta)) return prev
      return [...prev, cuenta]
    })
  }, [])

  /**
   * Quita una cuenta de la lista de bajas pendientes (por ejemplo, luego de
   * que la baja se persistió con éxito en el backend).
   */
  const quitarEliminada = useCallback((cuenta) => {
    if (!cuenta) return
    const idCuenta = getIdCuenta(cuenta)
    setCuentasEliminadas((prev) => prev.filter((c) => getIdCuenta(c) !== idCuenta))
  }, [])

  /** Descarta todas las operaciones pendientes (botón "Cancelar"). */
  const limpiarPendientes = useCallback(() => {
    setCuentasEliminadas([])
  }, [])

  const hasCuentasPendientes = useMemo(() => cuentasEliminadas.length > 0, [cuentasEliminadas])

  return {
    cuentasEliminadas,
    hasCuentasPendientes,
    marcarEliminada,
    quitarEliminada,
    limpiarPendientes,
  }
}
